import React, { useState, useEffect } from 'react';
import {
  BellOutlined,
  MessageOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  FileTextOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { Badge, Dropdown, List, Button, Divider, Empty, Tooltip } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useNotificationStore } from '@stores/notificationStore';

// 扩展 dayjs 以支持相对时间
dayjs.extend(relativeTime);

// 通知类型图标映射
const notificationIcons = {
  notification: <BellOutlined className="text-blue-500" />,
  chat: <MessageOutlined className="text-green-500" />,
  system: <SettingOutlined className="text-orange-500" />,
  task: <FileTextOutlined className="text-purple-500" />,
  project: <TeamOutlined className="text-red-500" />,
};

// 通知项目组件
const NotificationItem: React.FC<{
  id: string;
  type: string;
  title: string;
  content: string;
  senderName?: string;
  createdAt: string;
  read: boolean;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
}> = ({
  id,
  type,
  title,
  content,
  senderName,
  createdAt,
  read,
  onMarkAsRead,
  onDelete,
}) => {
  return (
    <List.Item
      key={id}
      className={`cursor-pointer transition-all duration-200 ${!read ? 'bg-blue-50' : ''}`}
      onClick={() => !read && onMarkAsRead(id)}
    >
      <List.Item.Meta
        avatar={notificationIcons[type as keyof typeof notificationIcons] || <BellOutlined />}
        title={
          <div className="flex justify-between items-center">
            <span className={`font-medium ${!read ? 'font-semibold' : ''}`}>{title}</span>
            <span className="text-xs text-gray-400 ml-2">{dayjs(createdAt).fromNow()}</span>
          </div>
        }
        description={
          <div className="text-sm">
            <p className="text-gray-600 mb-1">{content}</p>
            {senderName && (
              <p className="text-xs text-gray-500">发送者: {senderName}</p>
            )}
          </div>
        }
      />
      <div className="flex items-center space-x-1">
        {!read && (
          <Tooltip title="标记为已读">
            <Button
              type="text"
              size="small"
              icon={<CheckCircleOutlined className="text-green-500" />}
              onClick={(e) => {
                e.stopPropagation();
                onMarkAsRead(id);
              }}
            />
          </Tooltip>
        )}
        <Tooltip title="删除">
          <Button
            type="text"
            size="small"
            danger
            icon={<CloseCircleOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(id);
            }}
          />
        </Tooltip>
      </div>
    </List.Item>
  );
};

// 通知下拉组件
export const NotificationDropdown: React.FC = () => {
  const {
    messages,
    unreadCount,
    loading,
    fetchMessages,
    markAsRead,
    deleteMessage,
    markAllAsRead,
    clearAllMessages,
  } = useNotificationStore();
  
  const [visible, setVisible] = useState(false);

  // 当下拉菜单打开时，获取消息
  useEffect(() => {
    if (visible) {
      fetchMessages();
    }
  }, [visible, fetchMessages]);

  // 菜单项
  const menuItems = (
    <div className="w-80 max-h-96 overflow-y-auto p-2">
      <div className="flex justify-between items-center mb-2 px-2">
        <h3 className="font-semibold text-lg">通知中心</h3>
        <div className="flex space-x-1">
          <Tooltip title="标记所有为已读">
            <Button
              type="text"
              size="small"
              onClick={markAllAsRead}
              disabled={messages.length === 0}
            >
              全部已读
            </Button>
          </Tooltip>
          <Tooltip title="清空所有通知">
            <Button
              type="text"
              size="small"
              danger
              onClick={clearAllMessages}
              disabled={messages.length === 0}
            >
              清空
            </Button>
          </Tooltip>
        </div>
      </div>
      
      <Divider className="my-2" />
      
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <LoadingOutlined spin />
        </div>
      ) : messages.length === 0 ? (
        <Empty description="暂无通知" className="py-8" />
      ) : (
        <List
          dataSource={messages}
          renderItem={(message) => (
            <NotificationItem
              id={message.id}
              type={message.type}
              title={message.title}
              content={message.content}
              senderName={message.senderName}
              createdAt={message.createdAt}
              read={message.read}
              onMarkAsRead={markAsRead}
              onDelete={deleteMessage}
            />
          )}
        />
      )}
      
      {messages.length > 0 && (
        <div className="mt-2 text-center">
          <Button
            type="link"
            size="small"
            className="text-blue-600"
            // onClick={() => navigate('/notifications')} // 后续可以跳转到通知详情页
          >
            查看全部
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Dropdown
      menu={{ items: [{
        key: 'notifications',
        label: menuItems
      }] }}
      trigger={['click']}
      onOpenChange={setVisible}
      placement="bottomRight"
    >
      <Badge count={unreadCount} size="small">
        <Button
          type="text"
          icon={<BellOutlined />}
          className="text-gray-600 hover:text-blue-600"
        />
      </Badge>
    </Dropdown>
  );
};

// 通知徽章组件（用于显示未读数量）
export const NotificationBadge: React.FC = () => {
  const { unreadCount } = useNotificationStore();

  return (
    <Badge count={unreadCount} size="small">
      <BellOutlined className="text-gray-600" />
    </Badge>
  );
};